import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { AuthShell } from "@/components/AuthShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api, { formatApiError } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ShieldCheck } from "lucide-react";

export default function InvitationAcceptPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [params] = useSearchParams();
  const token = params.get("token") || "";

  const [invite, setInvite] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | ready | invalid
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token) { setStatus("invalid"); return; }
    api.get(`/team/invitation/${token}`)
      .then(({ data }) => { setInvite(data); setName(data.name || ""); setStatus("ready"); })
      .catch(() => setStatus("invalid"));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) { setError(t("dash.pages.acceptInvite.mismatch")); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/team/accept", { token, name, password });
      localStorage.setItem("pg_token", data.token);
      toast.success(t("dash.pages.acceptInvite.success"));
      await refreshUser();
      navigate("/app");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || t("dash.pages.acceptInvite.invalid"));
    } finally {
      setLoading(false);
    }
  };

  if (status === "invalid") {
    return (
      <AuthShell title={t("dash.pages.acceptInvite.title")} subtitle={t("dash.pages.acceptInvite.invalid")}>
        <div className="text-center" data-testid="invite-invalid">
          <Link to="/" className="text-sm text-white/50 hover:text-[#EAB308] transition-colors">
            {t("dash.pages.acceptInvite.backHome")}
          </Link>
        </div>
      </AuthShell>
    );
  }

  const subtitle = invite
    ? `${t("dash.pages.acceptInvite.subtitle")} ${invite.company_name}`
    : "…";

  return (
    <AuthShell title={t("dash.pages.acceptInvite.title")} subtitle={subtitle}>
      {status === "loading" ? (
        <div className="flex justify-center py-8">
          <div className="w-8 h-8 rounded-full border-2 border-[#EAB308]/30 border-t-[#EAB308] animate-spin" />
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-5" data-testid="accept-invite-form">
          <div className="flex items-center gap-3 rounded-md border border-[#EAB308]/30 bg-[#EAB308]/[0.06] px-4 py-3">
            <ShieldCheck className="w-5 h-5 text-[#EAB308] shrink-0" />
            <div className="min-w-0">
              <div className="text-sm text-white truncate" data-testid="invite-email">{invite.email}</div>
              <div className="text-xs text-white/45">{t(`dash.pages.team.roles.${invite.role}`)}</div>
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-white/80">{t("dash.pages.acceptInvite.name")}</Label>
            <Input data-testid="accept-name" required value={name} onChange={(e) => setName(e.target.value)} className="bg-[#0a0a0a] border-white/15 text-white h-11" />
          </div>
          <div className="space-y-2">
            <Label className="text-white/80">{t("dash.pages.acceptInvite.password")}</Label>
            <Input data-testid="accept-password" type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} className="bg-[#0a0a0a] border-white/15 text-white h-11" />
          </div>
          <div className="space-y-2">
            <Label className="text-white/80">{t("dash.pages.acceptInvite.confirm")}</Label>
            <Input data-testid="accept-confirm" type="password" required minLength={6} value={confirm} onChange={(e) => setConfirm(e.target.value)} className="bg-[#0a0a0a] border-white/15 text-white h-11" />
          </div>
          {error && (
            <div data-testid="accept-error" className="rounded-md border border-[#dc143c]/40 bg-[#dc143c]/10 px-4 py-2.5 text-sm text-red-300">{error}</div>
          )}
          <Button data-testid="accept-submit" type="submit" disabled={loading} className="w-full h-11 bg-[#EAB308] text-black font-semibold rounded-md hover:opacity-90 transition-opacity">
            {loading ? t("dash.pages.acceptInvite.submitting") : t("dash.pages.acceptInvite.submit")}
          </Button>
        </form>
      )}
    </AuthShell>
  );
}
